import isEmpty from 'lodash/isEmpty'
import { useLatest } from 'react-use'
import type {
  BatchAssignChatsArgs,
  BatchCancelChatsArgs,
  BatchCloseChatsArgs,
  BatchMarkChatsAsSpamArgs,
  BatchMarkChatsReadArgs,
  BatchReplyToChatsArgs,
  BatchUpdateChatRemarksArgs,
  BatchUpdateChatTagsArgs,
  ChatBatchReplyInput,
  ChatBatchResult,
} from '@ztechapps/common.contract'
import { useActionMutation, useError, useToast } from '@ztechapps/common.hook'
import {
  batchAssignChatsMutation,
  batchCancelChatsMutation,
  batchCloseChatsMutation,
  batchMarkChatsAsSpamMutation,
  batchMarkChatsReadMutation,
  batchReplyToChatsMutation,
  batchUpdateChatRemarksMutation,
  batchUpdateChatTagsMutation,
} from './graphql'
import { useConversationInboxContext } from './Context'
import { reportBatchOutcome } from './reportBatchOutcome'

export type UseConversationBatchActionsResult = {
  closeConversations: (chatIds: string[]) => Promise<void>
  cancelConversations: (chatIds: string[]) => Promise<void>
  markSpamConversations: (chatIds: string[]) => Promise<void>
  markReadConversations: (chatIds: string[]) => Promise<void>
  assignConversations: (chatIds: string[], assigneeId?: string) => Promise<void>
  updateTags: (chatIds: string[], tags: string[]) => Promise<void>
  updateRemarks: (chatIds: string[], remarks: string) => Promise<void>
  replyToConversations: (replies: ChatBatchReplyInput[]) => Promise<void>
  closing: boolean
  canceling: boolean
  markingSpam: boolean
  markingRead: boolean
  assigning: boolean
  updatingTags: boolean
  updatingRemarks: boolean
  replying: boolean
}

export const useConversationBatchActions = (): UseConversationBatchActionsResult => {
  const { chatList, chatListWithSelection, onRefresh } = useConversationInboxContext()
  const { showSuccess, showError } = useToast()

  const [closeExecute, { loading: closing, error: closeError }] = useActionMutation<
    ChatBatchResult,
    BatchCloseChatsArgs
  >({
    query: batchCloseChatsMutation,
  })
  const [cancelExecute, { loading: canceling, error: cancelError }] = useActionMutation<
    ChatBatchResult,
    BatchCancelChatsArgs
  >({ query: batchCancelChatsMutation })
  const [markSpamExecute, { loading: markingSpam, error: markSpamError }] = useActionMutation<
    ChatBatchResult,
    BatchMarkChatsAsSpamArgs
  >({ query: batchMarkChatsAsSpamMutation })
  const [markReadExecute, { loading: markingRead, error: markReadError }] = useActionMutation<
    ChatBatchResult,
    BatchMarkChatsReadArgs
  >({ query: batchMarkChatsReadMutation })
  const [assignExecute, { loading: assigning, error: assignError }] = useActionMutation<
    ChatBatchResult,
    BatchAssignChatsArgs
  >({ query: batchAssignChatsMutation })
  const [updateTagsExecute, { loading: updatingTags, error: updateTagsError }] = useActionMutation<
    ChatBatchResult,
    BatchUpdateChatTagsArgs
  >({ query: batchUpdateChatTagsMutation })
  const [updateRemarksExecute, { loading: updatingRemarks, error: updateRemarksError }] = useActionMutation<
    ChatBatchResult,
    BatchUpdateChatRemarksArgs
  >({ query: batchUpdateChatRemarksMutation })
  const [replyExecute, { loading: replying, error: replyError }] = useActionMutation<
    ChatBatchResult,
    BatchReplyToChatsArgs
  >({ query: batchReplyToChatsMutation })

  useError(closeError)
  useError(cancelError)
  useError(markSpamError)
  useError(markReadError)
  useError(assignError)
  useError(updateTagsError)
  useError(updateRemarksError)
  useError(replyError)

  const reportOutcome = (noun: string, verb: string, result: ChatBatchResult) =>
    reportBatchOutcome({ showSuccess, showError }, noun, verb, result)

  // The selection hook only re-syncs when the list's content changes, and the list's `updated`
  // subscription can land a batch's changes while the mutation is still awaiting. The runner's
  // closure holds the rows from the render that started the batch, so resetting from `chatList.list`
  // there would put the pre-batch rows back and the refetch — deep-equal to the cache by then —
  // would never replace them. Read the latest rows instead.
  const latestRows = useLatest(chatList.list)
  const clearSelection = () => {
    chatListWithSelection.reset(latestRows.current ?? [])
  }

  // Every batch action runs the same way: execute, report the per-chat outcome, then clear the
  // selection and refetch the list whether or not it threw.
  const runBatch = async (noun: string, verb: string, execute: () => Promise<ChatBatchResult>) => {
    try {
      const result = await execute()
      if (!isEmpty(result)) reportOutcome(noun, verb, result)
    } finally {
      clearSelection()
      await onRefresh()
    }
  }

  const closeConversations = (chatIds: string[]) => runBatch('conversation', 'closed', () => closeExecute({ chatIds }))

  const cancelConversations = (chatIds: string[]) =>
    runBatch('conversation', 'cancelled', () => cancelExecute({ chatIds }))

  // Marking is reversible, so it runs with no confirmation — the shared runner still isolates each
  // conversation's failure and the list still refreshes either way.
  const markSpamConversations = (chatIds: string[]) =>
    runBatch('conversation', 'marked as spam', () => markSpamExecute({ chatIds }))

  const markReadConversations = (chatIds: string[]) =>
    runBatch('conversation', 'marked as read', () => markReadExecute({ chatIds }))

  const assignConversations = (chatIds: string[], assigneeId?: string) =>
    runBatch('conversation', 'assigned', () => assignExecute({ chatIds, assigneeId: assigneeId ?? null }))

  const updateTags = (chatIds: string[], tags: string[]) =>
    runBatch('conversation', 'updated (tags)', () => updateTagsExecute({ chatIds, tags }))

  const updateRemarks = (chatIds: string[], remarks: string) =>
    runBatch('conversation', 'updated (remarks)', () => updateRemarksExecute({ chatIds, remarks }))

  const replyToConversations = (replies: ChatBatchReplyInput[]) =>
    runBatch('message', 'sent', () => replyExecute({ replies }))

  return {
    closeConversations,
    cancelConversations,
    markSpamConversations,
    markReadConversations,
    assignConversations,
    updateTags,
    updateRemarks,
    replyToConversations,
    closing,
    canceling,
    markingSpam,
    markingRead,
    assigning,
    updatingTags,
    updatingRemarks,
    replying,
  }
}
